/**
 * 消息操作工具函数
 * 提供统一的消息查找、更新和processLog操作方法
 */

import { Message, ProcessStep } from '../../types';

// 消息操作工具函数
export const messageUtils = {
  // 快速获取消息的便捷函数
  getMessage: (
    messages: Record<string, Message[]>,
    conversationId: string,
    messageId: string
  ): { message: Message | null; messageIndex: number; messages: Message[] } => {
    const conversationMessages = messages[conversationId] || [];
    const messageIndex = conversationMessages.findIndex(m => m.id === messageId);

    return {
      message: messageIndex !== -1 ? conversationMessages[messageIndex] : null,
      messageIndex,
      messages: conversationMessages
    };
  },

  // 更新消息的便捷函数
  updateMessage: (
    prevMessages: Record<string, Message[]>,
    conversationId: string,
    messageId: string,
    updater: (message: Message) => Message
  ): Record<string, Message[]> => {
    const { message, messageIndex, messages } = messageUtils.getMessage(
      prevMessages,
      conversationId,
      messageId
    );

    if (!message || messageIndex === -1) return prevMessages;

    const updatedMessages = [...messages];
    updatedMessages[messageIndex] = updater(message);

    return { ...prevMessages, [conversationId]: updatedMessages };
  },

  // 更新processLog的便捷函数
  updateProcessLog: (
    prevMessages: Record<string, Message[]>,
    conversationId: string,
    messageId: string,
    processLogUpdater: (processLog: ProcessStep[]) => ProcessStep[]
  ): Record<string, Message[]> => {
    return messageUtils.updateMessage(prevMessages, conversationId, messageId, (message) => ({
      ...message,
      processStep: processLogUpdater(message.processStep || []),
      contentUpdateTime: new Date().toISOString(), // 保留用于触发渲染更新
    }));
  },

  // 删除多个消息的便捷函数
  removeMessages: (
    prevMessages: Record<string, Message[]>,
    conversationId: string,
    messageIds: string[]
  ): Record<string, Message[]> => {
    const conversationMessages = prevMessages[conversationId] || [];
    const filteredMessages = conversationMessages.filter(m => !messageIds.includes(m.id));

    return { ...prevMessages, [conversationId]: filteredMessages };
  },

  // 批量添加消息的便捷函数
  addMessages: (
    prevMessages: Record<string, Message[]>,
    conversationId: string,
    newMessages: Message[]
  ): Record<string, Message[]> => {
    const conversationMessages = prevMessages[conversationId] || [];

    return { ...prevMessages, [conversationId]: [...conversationMessages, ...newMessages] };
  }

  // 注意：findConversationId 函数已移除
  // 现在所有事件都包含 conversationId，无需查找
};
